import type { ApiClient } from '../../core/api-client.js';
import { ApiError } from '../../core/api-client.js';
import type { Resultado } from '../../core/resultado.js';
import type { Insumo, NovoInsumo, PatchInsumo, Fornecedor, NovoFornecedor, Movimentacao, NovaMovimentacao, FiltroMovimentacao } from '@pgcustom/shared-types';

/**
 * Adapter do módulo Estoque no frontend — único ponto que conhece o
 * formato das rotas `/api/v1/estoque/*`
 * (`apps/api/src/modules/estoque/estoque.routes.ts`). Mesmo padrão de
 * `ParceirosApi`: listagens devolvem `Resultado<T>`; escrita propaga
 * `ApiError` — quem decide como exibir a falha é a tela que chama.
 */
export class EstoqueApi {
  constructor(private readonly api: ApiClient) {}

  async listarInsumos(): Promise<Resultado<Insumo[]>> {
    try {
      const resposta = await this.api.get<{ insumos: Insumo[] }>('/estoque/insumos');
      if (resposta.insumos.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.insumos };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar insumos' };
    }
  }

  async criarInsumo(dado: NovoInsumo): Promise<Insumo> {
    const resposta = await this.api.post<{ insumo: Insumo }>('/estoque/insumos', dado);
    return resposta.insumo;
  }

  async atualizarInsumo(id: string, dado: PatchInsumo): Promise<Insumo> {
    const resposta = await this.api.patch<{ insumo: Insumo }>(`/estoque/insumos/${id}`, dado);
    return resposta.insumo;
  }

  async listarFornecedores(): Promise<Resultado<Fornecedor[]>> {
    try {
      const resposta = await this.api.get<{ fornecedores: Fornecedor[] }>('/estoque/fornecedores');
      if (resposta.fornecedores.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.fornecedores };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar fornecedores' };
    }
  }

  async criarFornecedor(dado: NovoFornecedor): Promise<Fornecedor> {
    const resposta = await this.api.post<{ fornecedor: Fornecedor }>('/estoque/fornecedores', dado);
    return resposta.fornecedor;
  }

  async listarMovimentacoes(filtro: FiltroMovimentacao = {}): Promise<Resultado<Movimentacao[]>> {
    try {
      const resposta = await this.api.get<{ movimentacoes: Movimentacao[] }>('/estoque/movimentacoes', { ...filtro });
      if (resposta.movimentacoes.length === 0) return { status: 'vazio' };
      return { status: 'ok', dado: resposta.movimentacoes };
    } catch (erro) {
      if (erro instanceof ApiError && erro.status === 403) return { status: 'sem-permissao' };
      return { status: 'erro', mensagem: erro instanceof ApiError ? erro.message : 'falha ao carregar movimentações' };
    }
  }

  async registrarMovimentacao(dado: NovaMovimentacao): Promise<Movimentacao> {
    const resposta = await this.api.post<{ movimentacao: Movimentacao }>('/estoque/movimentacoes', dado);
    return resposta.movimentacao;
  }
}
